import React, { useMemo, useCallback, useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import { useUserRole } from "@/services/userServices/useUserRole";
import RequiredPagesView from "./RequiredPagesView";
import usePageDeleteConfirm from "../usePageDeleteConfirm";
import { addRequiredPage } from "@/services/pageServices/pageAction";
import { getTemplatesByDesign } from "@/services/templateServices/templateAction";
import { toPageContentEntry } from "../pageContentEntry";
import type { PageContentEntry } from "../pageContentEntry";

/** A content variable as the template declares it. */
type TemplateVariable = { variable?: string; type?: string; complextype?: string };

/** A navigation/footer template from templates_by_design_site, as far as this block reads it. */
type RequiredPageTemplate = {
  _id?: unknown;
  name?: string;
  type?: string;
  content_variables?: {
    ContentRequired?: TemplateVariable[];
    ContentOptional?: TemplateVariable[];
  };
};

type TemplateOption = { value: RequiredPageTemplate } | null;

/** Either an input's change event or a react-select option (`{ label, value }`). */
type VariableChangeEvent = {
  target?: { name: string; value: unknown };
  label?: string;
  value?: unknown;
} | null;

type RequiredPageType = "navigation" | "footer";

// Initial page_content for a picked template: every ContentRequired /
// ContentOptional variable with the empty value its type starts from. Pure, so it
// lives at module scope next to the handler factory that calls it.
const preparePageContent = (template: RequiredPageTemplate | null): PageContentEntry[] => {
  if (!template?.content_variables) return [];

  // Helper to get initial value based on type
  const getInitialValue = (v: TemplateVariable) => {
    if (v.type === "Boolean") return false;
    if (v.type === "Link") return { name: "", link: "" };
    if (v.type === "Array") {
      // For Array types, initialize as array
      if (v.complextype === "links") return [{ name: "", link: "" }];
      return [];
    }
    return "";
  };

  // SCRUM-1514: the variable and the empty value its type starts from, and
  // nothing else. `getInitialValue` still reads the template's `type` and
  // `complextype` — that is deriving FROM the template, which is the point; what
  // stops is copying those fields onto the entry that gets stored.
  // RequiredPagesView resolves them back for rendering.
  const required = (template.content_variables.ContentRequired || []).map((v) =>
    toPageContentEntry(v, getInitialValue(v))
  );
  const optional = (template.content_variables.ContentOptional || []).map((v) =>
    toPageContentEntry(v, getInitialValue(v))
  );
  return [...required, ...optional];
};

// One parametrized handler set per column (SCRUM-872). Navigation and Footer ran
// two literal copies of every handler — fourteen functions — differing only in
// which setter they called. The setters are the only thing a column needs, so a
// factory over them leaves the view's prop contract untouched while there is a
// single implementation to read and to change.
const makeColumnHandlers = (
  setSelectedTemplate: React.Dispatch<React.SetStateAction<RequiredPageTemplate | null>>,
  setPageContent: React.Dispatch<React.SetStateAction<PageContentEntry[]>>
) => ({
  templateChange: (option: TemplateOption) => {
    const template = option ? option.value : null;
    setSelectedTemplate(template);
    setPageContent(template ? preparePageContent(template) : []);
  },

  variableChange: (e: VariableChangeEvent, action?: { name?: string }) => {
    setPageContent((prev) => {
      const content = [...prev];
      // input (string/number)
      const target = e?.target;
      if (target) {
        const idx = content.findIndex((cv) => cv.variable === target.name);
        if (idx >= 0) content[idx] = { ...content[idx], value: target.value };
      }
      // boolean select
      if (e?.label && action?.name) {
        const idx = content.findIndex((cv) => cv.variable === action.name);
        if (idx >= 0) content[idx] = { ...content[idx], value: e.value };
      }
      return content;
    });
  },

  // Link type: merge one half ("name" | "link") into the stored pair.
  linkChange: (event: React.ChangeEvent<HTMLInputElement>, targetVariableName: string) => {
    const key = event.target.name;
    const val = event.target.value;
    setPageContent((prev) =>
      prev.map((cv) =>
        cv.variable === targetVariableName
          ? { ...cv, value: { ...((cv.value as Record<string, unknown> | undefined) || {}), [key]: val } }
          : cv
      )
    );
  },

  // Array:links. Add, delete and link-change all overwrite `value` with the array
  // the editor hands over, so they are one operation today and one function here.
  // Split it again only when a caller needs them to differ.
  arrayChange: (linksArray: unknown[], varName: string) => {
    setPageContent((prev) =>
      prev.map((cv) =>
        cv.variable === varName ? { ...cv, value: linksArray } : cv
      )
    );
  },
});

/**
 * Under `strictNullChecks` a `= undefined` default alone types the prop as
 * `undefined`, so `deletePageOperation(page)` would not be callable; the type
 * declares what DisplayPagesView passes.
 */
export type RequiredPagesContainerProps = {
  navigate?: (path: string) => void;
  deletePageOperation?: ReturnType<typeof usePageDeleteConfirm>["confirmDelete"];
};

const RequiredPagesContainer = ({
  navigate = (path: string) => (window.location.href = path),
  deletePageOperation = undefined,
}: RequiredPagesContainerProps) => {
  const dispatch = useAppDispatch();
  const CurrentSite = useAppSelector((s) => s.sites.site);
  const permission = useCurrentPermission();
  const role = useUserRole();
  const templatesByDesign = useAppSelector((s) => s.templates.templates_by_design_site || []);

  const [selectedNavTemplate, setSelectedNavTemplate] = useState<RequiredPageTemplate | null>(null);
  const [selectedFooterTemplate, setSelectedFooterTemplate] = useState<RequiredPageTemplate | null>(null);
  const [navPageContent, setNavPageContent] = useState<PageContentEntry[]>([]);
  const [footerPageContent, setFooterPageContent] = useState<PageContentEntry[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  const requiredPages = useMemo(() => CurrentSite?.required_pages || [], [CurrentSite?.required_pages]);

  // Check which types already exist
  const existingTypes = useMemo(() => {
    const types = new Set<string | undefined>();
    requiredPages.forEach((p) => types.add(p.type));
    return types;
  }, [requiredPages]);

  const hasNavigation = existingTypes.has("navigation");
  const hasFooter = existingTypes.has("footer");

  // Load templates when site design is available. `design` is an embedded
  // subdocument on the Site schema ({ _id, name, source } — server/models/site.ts),
  // not a ref, so there is no populated/unpopulated distinction to handle: the
  // server rebuilds it as an object on both write paths (controllers/site.js).
  // This read is the same expression every other site-design reader uses
  // (SCRUM-1443).
  const designId = CurrentSite?.design?._id;

  useEffect(() => {
    if (designId) {
      dispatch(getTemplatesByDesign(designId));
    }
  }, [dispatch, designId]);

  // Filter templates for navigation and footer
  // Fallback to all templates if no specific matches found
  const navigationTemplates = useMemo(() => {
    const filtered = templatesByDesign.filter(
      (t) => t.type === "header" || t.type === "navigation" || t.name?.toLowerCase().includes("header") || t.name?.toLowerCase().includes("navigation")
    );
    // If no navigation templates found, return all templates as fallback
    return filtered.length > 0 ? filtered : templatesByDesign;
  }, [templatesByDesign]);

  const footerTemplates = useMemo(() => {
    const filtered = templatesByDesign.filter(
      (t) => t.type === "footer" || t.name?.toLowerCase().includes("footer")
    );
    // If no footer templates found, return all templates as fallback
    return filtered.length > 0 ? filtered : templatesByDesign;
  }, [templatesByDesign]);

  const handleEditRequired = useCallback(
    (pageId: string) => {
      navigate(`/admin/update-required-pg/${pageId}`);
    },
    [navigate]
  );

  // Every delete asks first (SCRUM-2021): the Pages index passes its own confirm
  // operation, and without one (the Create Page screen) this block uses the
  // same dialog itself, toasting through the shell hub because that screen has
  // no toast container of its own while this block is shown. There is no path
  // that deletes on the first click.
  const ownConfirm = usePageDeleteConfirm({ toHub: true });
  const confirmDelete = deletePageOperation || ownConfirm.confirmDelete;
  const handleDelete = useCallback(
    (page: Parameters<typeof confirmDelete>[0], e?: { preventDefault?: () => void }) => {
      e?.preventDefault?.();
      confirmDelete(page);
    },
    [confirmDelete]
  );

  // useState setters are stable, so one factory call per column survives every
  // render — the handler props keep the identity they had when each was its own
  // useCallback([]).
  const nav = useMemo(
    () => makeColumnHandlers(setSelectedNavTemplate, setNavPageContent),
    []
  );
  const footer = useMemo(
    () => makeColumnHandlers(setSelectedFooterTemplate, setFooterPageContent),
    []
  );

  const handleCreate = useCallback(
    async (type: RequiredPageType) => {
      const isNav = type === "navigation";
      const template = isNav ? selectedNavTemplate : selectedFooterTemplate;
      if (!template) return;
      setIsCreating(true);
      try {
        await dispatch(
          addRequiredPage({
            type,
            page_template: template,
            page_content: isNav ? navPageContent : footerPageContent,
          })
        );
        (isNav ? setSelectedNavTemplate : setSelectedFooterTemplate)(null);
        (isNav ? setNavPageContent : setFooterPageContent)([]);
      } finally {
        setIsCreating(false);
      }
    },
    [
      dispatch,
      selectedNavTemplate,
      navPageContent,
      selectedFooterTemplate,
      footerPageContent,
    ]
  );

  const handleCreateNavigation = useCallback(
    () => handleCreate("navigation"),
    [handleCreate]
  );
  const handleCreateFooter = useCallback(
    () => handleCreate("footer"),
    [handleCreate]
  );

  return (
    <>
      {/* Only this block's own dialog; the Pages index renders its own. */}
      {!deletePageOperation && ownConfirm.confirmElement}
      <RequiredPagesView
        pages={requiredPages}
        role={role}
        permission={permission}
        onEditRequired={handleEditRequired}
        onDeletePage={handleDelete}
        // Creation props
        hasNavigation={hasNavigation}
        hasFooter={hasFooter}
        navigationTemplates={navigationTemplates}
        footerTemplates={footerTemplates}
        selectedNavTemplate={selectedNavTemplate}
        selectedFooterTemplate={selectedFooterTemplate}
        navPageContent={navPageContent}
        footerPageContent={footerPageContent}
        onNavTemplateChange={nav.templateChange}
        onFooterTemplateChange={footer.templateChange}
        onNavVariableChange={nav.variableChange}
        onFooterVariableChange={footer.variableChange}
        onNavLinkChange={nav.linkChange}
        onFooterLinkChange={footer.linkChange}
        onNavArrayAdd={nav.arrayChange}
        onNavArrayDelete={nav.arrayChange}
        onNavArrayLinkChange={nav.arrayChange}
        onFooterArrayAdd={footer.arrayChange}
        onFooterArrayDelete={footer.arrayChange}
        onFooterArrayLinkChange={footer.arrayChange}
        onCreateNavigation={handleCreateNavigation}
        onCreateFooter={handleCreateFooter}
        isCreating={isCreating}
      />
    </>
  );
};

export default RequiredPagesContainer;
